import React from "react";
import { ShieldCheck, Headphones, Zap, Crown } from "lucide-react";

/**
 * Payment Trust Strip — payment + payout method logos with "Same Day Redemption" tagline.
 * Designed to be dropped on landing page, store, and redeem page to project legitimacy.
 *
 * Active redemption rails (debit/card rails removed — not currently supported):
 *  - Crypto = NowPayments (live) — Bitcoin / USDC on Polygon
 *  - Bank Transfer = Zelle + Wire (live, manual review)
 *  - Gift Cards = Giftbit (live, instant)
 */
export const PaymentTrustStrip = ({ compact = false, className = "" }) => {
  const methods = [
    { label: "Bitcoin", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/Bitcoin.svg/2048px-Bitcoin.svg.png" },
    { label: "USDC", logo: "https://cryptologos.cc/logos/usd-coin-usdc-logo.png" },
    { label: "Bank Transfer", logo: "https://cdn-icons-png.flaticon.com/512/2830/2830284.png" },
    { label: "Gift Cards", logo: "https://cdn-icons-png.flaticon.com/512/3081/3081893.png" },
  ];

  return (
    <section className={`bg-gradient-to-b from-[#0b1220] to-[#020617] py-10 sm:py-14 ${className}`} data-testid="payment-trust-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {!compact && (
          <>
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
              <Zap className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-bold text-emerald-300 uppercase tracking-wider">Same-Day Redemption</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
              Cash Out, <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Your Way</span>
            </h3>
            <p className="text-sm text-slate-400 mb-8 max-w-2xl mx-auto">
              Redeem Sweeps Coins to your bank account, crypto wallet, or instant gift cards — no waiting, no hassle.
            </p>
          </>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
          {methods.map(m => (
            <div
              key={m.label}
              className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-center min-w-[88px] sm:min-w-[110px] h-14 sm:h-16 backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/20 transition-all"
              data-testid={`payment-logo-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              title={m.label}
            >
              <img src={m.logo} alt={m.label} className="max-h-6 sm:max-h-8 max-w-[80px] sm:max-w-[90px] object-contain" loading="lazy" onError={e => { e.target.style.display = "none"; e.target.parentElement.textContent = m.label; }} />
            </div>
          ))}
        </div>

        {!compact && (
          <p className="text-xs text-slate-500 mt-6">
            Redemptions processed through licensed financial partners. KYC verification required. Subject to platform terms.
          </p>
        )}
      </div>
    </section>
  );
};

/**
 * Trust signals grid — 4-up "Why Players Choose Us" pattern.
 */
export const TrustSignalsGrid = ({ className = "" }) => {
  const signals = [
    { icon: ShieldCheck, label: "Safe & Secure", desc: "Bank-level encryption. KYC verified. Licensed entertainment platform." },
    { icon: Headphones, label: "24/7 Support", desc: "AI-powered chat plus live human agents whenever you need us." },
    { icon: Zap, label: "Same-Day Redemption", desc: "Cash out fast — bank transfer, crypto, or gift cards." },
    { icon: Crown, label: "VIP Loyalty Club", desc: "Exclusive perks, rakeback, and elite-tier rewards as you play." },
  ];
  return (
    <section className={`py-12 sm:py-16 ${className}`} data-testid="trust-signals-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h3 className="text-2xl sm:text-4xl font-black text-white mb-3">
            Why Players <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Choose Us</span>
          </h3>
          <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
            Built for players who want fair games, fast cashouts, and a platform that treats them right.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {signals.map(s => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="bg-white/[0.04] border border-white/10 hover:border-amber-500/30 rounded-2xl p-5 sm:p-6 transition-all hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(251,191,36,0.1)]"
                data-testid={`trust-signal-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-amber-400" />
                </div>
                <h4 className="text-base sm:text-lg font-bold text-white mb-2">{s.label}</h4>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PaymentTrustStrip;
